'use client';

import { useFormatter, useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { AgentLineGenerateVideo } from '@/app/components/icons/AgentLineGenerateVideo';
import { BaseLineDelete } from '@/app/components/icons/BaseLineDelete';
import { BaseLineEdit } from '@/app/components/icons/BaseLineEdit';
import { BaseLineMore } from '@/app/components/icons/BaseLineMore';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/app/components/ui/dropdown-menu';
import { Input } from '@/app/components/ui/input';
import type { StudioV1ClipProject } from '@/app/types';
import { cn } from '@/app/utils';

const NAME_MAX_LENGTH = 80;

export interface ProjectCardProps {
  project: StudioV1ClipProject;
  onOpen: (id: string) => void;
  onRequestDelete: (project: StudioV1ClipProject) => void;
  onRename: (id: string, nextName: string) => Promise<boolean>;
}

export const ProjectCard = ({ project, onOpen, onRequestDelete, onRename }: ProjectCardProps) => {
  const t = useTranslations('videoEditor.list');
  const format = useFormatter();
  const [isRenaming, setIsRenaming] = useState(false);
  const [draftName, setDraftName] = useState(project.name ?? '');
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isRenaming) setDraftName(project.name ?? '');
  }, [project.name, isRenaming]);

  useEffect(() => {
    if (isRenaming) inputRef.current?.select();
  }, [isRenaming]);

  const projectId = project.id ?? '';
  const displayName = project.name?.trim() || t('untitled');
  const createdAt = project.created_at
    ? format.dateTime(new Date(project.created_at), { year: 'numeric', month: 'short', day: 'numeric' })
    : '';

  const handleStartRename = () => {
    setDraftName(project.name ?? '');
    setIsRenaming(true);
  };

  const handleCommitRename = async () => {
    if (!isRenaming || submitting) return;
    const next = draftName.trim();
    if (!next || next === (project.name ?? '').trim()) {
      setIsRenaming(false);
      setDraftName(project.name ?? '');
      return;
    }
    setSubmitting(true);
    const ok = await onRename(projectId, next);
    setSubmitting(false);
    if (ok) {
      setIsRenaming(false);
    } else {
      setDraftName(project.name ?? '');
      setIsRenaming(false);
    }
  };

  const handleCardClick = () => {
    if (isRenaming) return;
    onOpen(projectId);
  };

  return (
    <div
      className={cn(
        'group relative h-[136px] w-[240px] cursor-pointer overflow-hidden rounded-2xl',
        'border border-system-white08 bg-system-white04',
        'transition-colors hover:border-system-white16',
      )}
      onClick={handleCardClick}
    >
      {project.cover_url ? (
        <div
          className="absolute inset-0 bg-center bg-cover bg-no-repeat"
          style={{ backgroundImage: `url(${project.cover_url})` }}
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-system-white12">
          <AgentLineGenerateVideo className="size-5 text-system-white opacity-40" />
        </div>
      )}
      <div
        className={cn(
          'absolute inset-x-0 bottom-0 z-10 flex items-center justify-between gap-2 rounded-b-2xl p-3',
          'transition-[padding] duration-200',
        )}
        style={{
          background: 'linear-gradient(0deg, rgba(0, 0, 0, 0.64) 0%, rgba(0, 0, 0, 0.00) 100%)',
        }}
      >
        <div className="min-w-0 flex-1">
          {isRenaming ? (
            <Input
              ref={inputRef}
              value={draftName}
              autoFocus
              disabled={submitting}
              maxLength={NAME_MAX_LENGTH}
              onChange={(e) => setDraftName(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onBlur={handleCommitRename}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.currentTarget.blur();
                } else if (e.key === 'Escape') {
                  setIsRenaming(false);
                  setDraftName(project.name ?? '');
                }
              }}
              className="h-6 w-full rounded-4 border-system-white16 bg-system-black56 px-1.5 text-system-white text-xs"
            />
          ) : (
            <div className="truncate text-white text-xs">{displayName}</div>
          )}
          {createdAt && !isRenaming && (
            <div className="max-h-0 truncate text-white text-xs opacity-0 transition-[max-height,opacity] duration-200 group-hover:max-h-[17px] group-hover:opacity-30">
              {createdAt}
            </div>
          )}
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={t('moreActions')}
              onClick={(e) => e.stopPropagation()}
              className={cn(
                'flex size-6 shrink-0 items-center justify-center rounded-6 bg-black/50 p-0 text-system-white opacity-0 transition-opacity',
                'hover:bg-black/60 group-hover:opacity-100 data-[state=open]:opacity-100',
              )}
            >
              <BaseLineMore className="size-5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            side="right"
            className="w-[104px] rounded-8 border border-[#3B3B3B] bg-[#1A1A1A] px-[18px] py-5"
            onClick={(e) => e.stopPropagation()}
          >
            <DropdownMenuItem
              className="gap-1.5 px-0 py-0 text-sm text-system-white hover:bg-transparent aria-selected:bg-transparent"
              onClick={(e) => {
                e.stopPropagation();
                handleStartRename();
              }}
            >
              <BaseLineEdit className="mr-2 size-4" />
              {t('rename')}
            </DropdownMenuItem>
            <DropdownMenuItem
              className="mt-[26px] gap-1.5 px-0 py-0 text-sm text-system-white hover:bg-transparent aria-selected:bg-transparent"
              onClick={(e) => {
                e.stopPropagation();
                onRequestDelete(project);
              }}
            >
              <BaseLineDelete className="mr-2 size-4" />
              {t('deleteConfirm.primaryButton')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};
